import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { alpha, Backdrop, Link, Typography, styled, SHADOW_LEVEL1 } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { useCallback } from 'react';

import { JoinTileImage } from './JoinTileImage';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useNavigationContext } from '~/contexts/NavigationContext';

const JoinMenuRoot = styled('div')<{ ownerState: { isVisible: boolean } }>(
  ({ ownerState, theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
    display: 'none',
    ...(ownerState.isVisible && {
      [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
        boxShadow: SHADOW_LEVEL1,
        display: 'flex',
        flexDirection: 'column',
        left: 0,
        position: 'absolute',
        top: 'var(--top-nav-height)',
        transition: 'top var(--top-nav-height-animation)',
        width: '100%',
      },
    }),
  }),
);

const JoinTilesWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignItems: 'center',
    borderBottom: `6px solid ${theme.palette.common.white}`,
    columnGap: 6,
    display: 'grid',
    gridAutoColumns: 'minmax(325px, 680px)',
    gridAutoFlow: 'column',
    gridTemplateRows: 325,
    justifyContent: 'center',
    overflow: 'hidden',
  },
}));

const JoinTileLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    color: theme.palette.common.white,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',

    '&:hover, &:focus, &:active': {
      color: theme.palette.common.white,
      webkitTapHighlightColor: 'transparent',
    },

    '&:hover::before': {
      background: alpha(theme.palette.common.black, 0.3),
    },

    '&:hover': {
      '.joinTileTitle': {
        background: '#cda89c',
        borderColor: '#cda89c',
      },
    },

    '&::before': {
      background: 'transparent',
      content: '""',
      height: '100%',
      position: 'absolute',
      transition: '0.25s',
      width: '100%',
    },

    '& > img': {
      height: '100%',
      objectFit: 'cover',
      width: '100%',
    },
  },
}));

const JoinTileDescription = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(0, 6),
    overflow: 'hidden',
    position: 'absolute',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    textShadow: theme.shadows[1],
    top: '50%',
    transform: 'translateY(-150%)',
    whiteSpace: 'nowrap',
  },
}));

const JoinTileTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    border: `2px solid ${theme.palette.common.white}`,
    borderRadius: 23,
    fontSize: theme.typography.body1,
    fontWeight: theme.typography.fontWeightBold,
    left: '50%',
    lineHeight: theme.typography.pxToRem(41),
    maxWidth: 295,
    minWidth: 125,
    overflow: 'hidden',
    padding: theme.spacing(0, 6),
    position: 'absolute',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    textShadow: theme.shadows[1],
    top: '50%',
    transform: 'translate(-50%, 50%)',
    transition: '0.25s',
    whiteSpace: 'nowrap',
  },
}));

const JoinMenuIdkyLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    alignSelf: 'center',
    color: theme.palette.default.main,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(23),
    margin: theme.spacing(2, 0),
    textDecoration: 'underline',
    transition: '0.25s',

    '&:hover, &:focus, &:active': {
      color: theme.palette.default.light,
      webkitTapHighlightColor: 'transparent',
    },
  },
}));

export const JoinMenu: VFC = () => {
  const { activeMenu, iDontKnowYetLink, joinTiles, toggleMenu } = useNavigationContext();
  const exitMenu = useCallback(() => toggleMenu(''), [toggleMenu]);

  const { getTestId } = useTestId();

  if (joinTiles.length === 0) {
    return null;
  }

  const isMenuActive = activeMenu === ToggleMenuFlyout.MenuTypes.JoinMenu;
  const ownerState = { isVisible: isMenuActive };

  return (
    <JoinMenuRoot
      data-testid={getTestId('join-menu-root')}
      ownerState={ownerState}
    >
      <JoinTilesWrapper>
        {joinTiles.map(({ url, imageUrl, title, description }) => (
          <JoinTileLink
            key={url}
            data-testid={getTestId('join-menu-tile-link')}
            href={url}
          >
            {imageUrl ? (
              <JoinTileImage
                image={imageUrl}
                name={title}
              />
            ) : null}
            <JoinTileDescription
              data-testid={getTestId('join-menu-tile-description')}
              variant="h4"
            >
              {description}
            </JoinTileDescription>
            <JoinTileTitle
              className="joinTileTitle"
              variant="h6"
            >
              {title}
            </JoinTileTitle>
          </JoinTileLink>
        ))}
      </JoinTilesWrapper>
      {iDontKnowYetLink?.url ? (
        <JoinMenuIdkyLink href={iDontKnowYetLink.url}>{iDontKnowYetLink.title}</JoinMenuIdkyLink>
      ) : null}
      <Backdrop
        open
        data-testid={getTestId('join-menu-backdrop')}
        sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
        onClick={exitMenu}
      />
    </JoinMenuRoot>
  );
};
